import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Heart, Mail, Lock, Ban, Send, CheckCircle, HelpCircle, LogIn, Briefcase } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { login, submitSuspensionInquiry, submitWeddingSuspensionInquiry, resendVerification, forgotPassword, removeHelperRole, switchRole } from '../services/api';
import { isNative, openGoogleSignIn } from '../utils/native';

// Default landing route for each role/dashboard
const ROLE_HOME = { couple: '/dashboard', helper: '/checklist', vendor_employee: '/my-projects', vendor: '/vendor-portal', admin: '/admin' };

function Login() {
  const navigate = useNavigate();
  const { user, loading: authLoading, loginUser } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Password login hit an account created with Google (server code GOOGLE_ACCOUNT)
  const [googleAccount, setGoogleAccount] = useState(false);


  // Suspension state
  const [suspended, setSuspended] = useState(false);
  const [suspendedEmail, setSuspendedEmail] = useState('');
  const [suspendedBusiness, setSuspendedBusiness] = useState('');
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);

  // Wedding suspension state
  const [weddingSuspended, setWeddingSuspended] = useState(false);
  const [weddingSuspendedEmail, setWeddingSuspendedEmail] = useState('');
  const [weddingSuspendedNames, setWeddingSuspendedNames] = useState('');
  const [weddingSuspendedReason, setWeddingSuspendedReason] = useState('');
  const [weddingInquiryMessage, setWeddingInquiryMessage] = useState('');
  const [weddingInquiryLoading, setWeddingInquiryLoading] = useState(false);
  const [weddingInquirySuccess, setWeddingInquirySuccess] = useState(false);

  // Email verification state
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [searchParams] = useSearchParams();
  // Set by pages that need a login first (e.g. Send Inquiry on a vendor page)
  // so the visitor comes back to them. Same-site paths only.
  const rawRedirect = searchParams.get('redirect');
  const redirectTo = rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') && !rawRedirect.includes('\\')
    ? rawRedirect : null;

  // Dual-role chooser state
  const [roleChooser, setRoleChooser] = useState(null); // { userData, token, availableRoles }
  const [roleSwitching, setRoleSwitching] = useState(false);

  // null until the auth check settles, then true if a session already existed
  const arrivedSignedIn = useRef(null);

  // Someone who arrives already signed in has no use for the login form — send
  // them to their dashboard. Keyed on the session that existed when the page
  // loaded, so logging in here (or picking a role) still follows its own redirect
  // rather than racing this one.
  useEffect(() => {
    if (authLoading) return;
    if (arrivedSignedIn.current === null) arrivedSignedIn.current = !!user;
    if (arrivedSignedIn.current && user) {
      navigate(redirectTo || ROLE_HOME[user.role] || '/dashboard', { replace: true });
    }
  }, [user, authLoading, redirectTo, navigate]);

  // Select a dashboard from the chooser. The base login token already carries
  // the user's primary role; for any other dashboard we ask the server for a
  // token scoped to that role so the whole app (navbar, guards, /me) follows.
  const chooseRole = async (role) => {
    if (!roleChooser || roleSwitching) return;
    setRoleSwitching(true);
    setError('');
    try {
      const { userData, token } = roleChooser;
      // Store the base session first so the API interceptor can authenticate.
      loginUser(userData, token);
      if (role !== userData.role) {
        const res = await switchRole(role);
        loginUser(res.data.user, res.data.token);
      }
      navigate(redirectTo || ROLE_HOME[role] || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not open that dashboard. Please try again.');
    } finally {
      setRoleSwitching(false);
    }
  };

  // Handle social login via popup postMessage or URL token fallback
  useEffect(() => {
    // Check URL params (fallback when popup didn't work)
    const urlToken = searchParams.get('token');
    const urlError = searchParams.get('error');
    if (urlError) {
      // searchParams already decodes; decoding again throws on a literal "%"
      setError(urlError);
      window.history.replaceState({}, '', '/login');
    }
    if (urlToken) {
      localStorage.setItem('nepwed_token', urlToken);
      window.history.replaceState({}, '', '/login');
      // Fetch user data with this token
      const api = require('../services/api');
      api.getMe().then(res => {
        const userData = res.data;
        const roles = userData.availableRoles || [userData.role];
        if (roles.length > 1) {
          setRoleChooser({ userData, token: urlToken, availableRoles: roles });
          return;
        }
        loginUser(userData, urlToken);
        navigate(redirectTo || ROLE_HOME[userData.role] || '/dashboard');
      }).catch(() => {
        localStorage.removeItem('nepwed_token');
        setError('Login failed. Please try again.');
      });
    }

    // Listen for popup postMessage
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'social-auth-error' && event.data.error) {
        setError(event.data.error);
        return;
      }
      if (event.data?.type === 'social-auth' && event.data.token && event.data.user) {
        const userData = event.data.user;
        const roles = userData.availableRoles || [userData.role];
        if (roles.length > 1) {
          setRoleChooser({ userData, token: event.data.token, availableRoles: roles });
          return;
        }
        loginUser(userData, event.data.token);
        navigate(redirectTo || ROLE_HOME[userData.role] || '/dashboard');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setGoogleAccount(false);
    setSuspended(false);
    setLoading(true);
    try {
      const res = await login(form);
      
      // If vendor_employee has a pending invite, redirect them to accept it.
      // Keep the session they just opened, so the invite page accepts it
      // straight away instead of asking for the same password a second time.
      if (res.data.hasPendingInvite) {
        const { inviteToken } = res.data.pendingInvite;
        loginUser(res.data.user, res.data.token);
        navigate(`/vendor-invite/accept/${inviteToken}`);
        return;
      }
      
      const userData = res.data.user;
      const availableRoles = userData.availableRoles || [userData.role];

      // If user has multiple roles, show role chooser
      if (availableRoles.length > 1) {
        setRoleChooser({ userData, token: res.data.token, availableRoles });
        setLoading(false);
        return;
      }

      loginUser(userData, res.data.token);

      // Role-based redirect
      if (redirectTo) {
        navigate(redirectTo);
      } else if (userData.role === 'admin') {
        navigate('/admin');
      } else if (userData.role === 'vendor') {
        navigate('/vendor-portal');
      } else if (userData.role === 'vendor_employee') {
        navigate('/my-projects');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.suspended) {
        setSuspended(true);
        setSuspendedEmail(data.vendorEmail);
        setSuspendedBusiness(data.businessName);
        setError('');
      } else if (data?.weddingSuspended) {
        setWeddingSuspended(true);
        setWeddingSuspendedEmail(data.coupleEmail);
        setWeddingSuspendedNames(data.weddingNames);
        setWeddingSuspendedReason(data.suspendedReason);
        setError('');
      } else if (data?.emailNotVerified) {
        setUnverifiedEmail(data.email);
        setError(data.error);
      } else if (data?.code === 'GOOGLE_ACCOUNT') {
        // The server text says "log in with Google", but the app has no Google button
        setGoogleAccount(true);
        setError(isNative() ? t('login.googleAccountNoGoogle') : t('login.googleAccountWeb'));
      } else {
        setError(data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryMessage.trim()) return;
    setInquiryLoading(true);
    try {
      await submitSuspensionInquiry({ vendorEmail: suspendedEmail, message: inquiryMessage });
      setInquirySuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit inquiry. Please try again.');
    } finally {
      setInquiryLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Heart className="auth-icon" size={32} />
            <h1>{t('login.welcomeBack')}</h1>
            <p>{t('login.signInContinue')}</p>
          </div>

          {/* Suspension Inquiry Form */}
          {suspended && !inquirySuccess && (
            <div className="suspension-notice">
              <div className="suspension-header">
                <Ban size={24} className="suspension-ban-icon" />
                <h2>Account Suspended</h2>
              </div>
              <p>Your vendor account <strong>"{suspendedBusiness}"</strong> has been suspended by the admin team. You cannot log in at this time.</p>
              <p>If you believe this is an error or would like to appeal, please submit an inquiry below:</p>
              <form onSubmit={handleInquirySubmit} className="suspension-form">
                <div className="form-group">
                  <label><Mail size={16} /> Your Email</label>
                  <input type="email" value={suspendedEmail} disabled />
                </div>
                <div className="form-group">
                  <label><Send size={16} /> Your Message</label>
                  <textarea
                    placeholder="Explain why your account should be reinstated..."
                    value={inquiryMessage}
                    onChange={(e) => setInquiryMessage(e.target.value)}
                    rows={4}
                    required
                  />
                </div>
                {error && <div className="auth-error">{error}</div>}
                <button type="submit" className="btn btn-primary btn-block" disabled={inquiryLoading}>
                  {inquiryLoading ? 'Submitting...' : 'Submit Inquiry to Admin'}
                </button>
                <button type="button" className="btn btn-secondary btn-block" onClick={() => { setSuspended(false); setError(''); }}>
                  Back to Login
                </button>
              </form>
            </div>
          )}

          {/* Inquiry Success */}
          {inquirySuccess && (
            <div className="suspension-notice success">
              <CheckCircle size={40} className="success-icon" />
              <h2>Inquiry Submitted</h2>
              <p>Your appeal has been sent to the admin team. They will review your case and get back to you shortly.</p>
              <button className="btn btn-primary btn-block" onClick={() => { setSuspended(false); setInquirySuccess(false); setInquiryMessage(''); }}>
                Back to Login
              </button>
            </div>
          )}

          {/* Wedding Suspension Notice */}
          {weddingSuspended && !weddingInquirySuccess && (
            <div className="suspension-notice">
              <div className="suspension-header">
                <Ban size={24} className="suspension-ban-icon" />
                <h2>Wedding Suspended</h2>
              </div>
              <p>The wedding <strong>"{weddingSuspendedNames}"</strong> has been suspended by the admin team. You cannot access your account at this time.</p>
              {weddingSuspendedReason && <p style={{ color: '#6b7280', fontSize: '14px' }}><strong>Reason:</strong> {weddingSuspendedReason}</p>}
              <p>If you believe this is an error or would like to appeal, please submit an inquiry below:</p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!weddingInquiryMessage.trim()) return;
                setWeddingInquiryLoading(true);
                try {
                  await submitWeddingSuspensionInquiry({ coupleEmail: weddingSuspendedEmail, message: weddingInquiryMessage });
                  setWeddingInquirySuccess(true);
                } catch (err) {
                  setError(err.response?.data?.error || 'Failed to submit inquiry.');
                } finally {
                  setWeddingInquiryLoading(false);
                }
              }} className="suspension-form">
                <div className="form-group">
                  <label><Mail size={16} /> Your Email</label>
                  <input type="email" value={weddingSuspendedEmail} disabled />
                </div>
                <div className="form-group">
                  <label><Send size={16} /> Your Message</label>
                  <textarea
                    placeholder="Explain why your wedding should be reinstated..."
                    value={weddingInquiryMessage}
                    onChange={(e) => setWeddingInquiryMessage(e.target.value)}
                    rows={4}
                    required
                  />
                </div>
                {error && <div className="auth-error">{error}</div>}
                <button type="submit" className="btn btn-primary btn-block" disabled={weddingInquiryLoading}>
                  {weddingInquiryLoading ? 'Submitting...' : 'Submit Inquiry to Admin'}
                </button>
                <button type="button" className="btn btn-secondary btn-block" onClick={() => { setWeddingSuspended(false); setError(''); }}>
                  Back to Login
                </button>
              </form>
            </div>
          )}

          {/* Wedding Inquiry Success */}
          {weddingInquirySuccess && (
            <div className="suspension-notice success">
              <CheckCircle size={40} className="success-icon" />
              <h2>Inquiry Submitted</h2>
              <p>Your appeal has been sent to the admin team. They will review your case and get back to you shortly.</p>
              <button className="btn btn-primary btn-block" onClick={() => { setWeddingSuspended(false); setWeddingInquirySuccess(false); setWeddingInquiryMessage(''); }}>
                Back to Login
              </button>
            </div>
          )}

          {/* Role Chooser for dual-role users */}
          {roleChooser && (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '8px', color: '#1a1a2e' }}>Choose Your Dashboard</h2>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
                Hi {roleChooser.userData.first_name}, you have access to multiple dashboards.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                {roleChooser.availableRoles.includes('couple') && (
                  <button
                    disabled={roleSwitching}
                    onClick={() => chooseRole('couple')}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', border: '2px solid #e5e7eb', borderRadius: '12px', background: '#fff', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#DC143C'; e.currentTarget.style.background = '#fef2f2'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <Heart size={28} style={{ color: '#DC143C', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#1a1a2e' }}>My Wedding</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Access your wedding dashboard, checklist, and guests</div>
                    </div>
                  </button>
                )}
                {roleChooser.availableRoles.includes('helper') && (
                  <button
                    disabled={roleSwitching}
                    onClick={() => chooseRole('helper')}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', border: '2px solid #e5e7eb', borderRadius: '12px', background: '#fff', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.background = '#eef2ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <HelpCircle size={28} style={{ color: '#6366f1', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#1a1a2e' }}>Helper Dashboard</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>Help with wedding tasks you've been assigned to</div>
                    </div>
                  </button>
                )}
                {roleChooser.availableRoles.includes('vendor_employee') && (
                  <button
                    disabled={roleSwitching}
                    onClick={() => chooseRole('vendor_employee')}
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', border: '2px solid #e5e7eb', borderRadius: '12px', background: '#fff', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#be185d'; e.currentTarget.style.background = '#fdf2f8'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <Briefcase size={28} style={{ color: '#be185d', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#1a1a2e' }}>Team Member Dashboard</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>View your assigned projects and team details</div>
                    </div>
                  </button>
                )}
              </div>
              <button
                onClick={() => setRoleChooser(null)}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
              >
                Back to Login
              </button>
            </div>
          )}

          {/* Normal Login Form */}
          {!suspended && !inquirySuccess && !weddingSuspended && !weddingInquirySuccess && !roleChooser && (
            <>


              {error && <div className="auth-error">{error}</div>}

              {googleAccount && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ fontSize: '13px', padding: '8px 16px' }}
                    onClick={() => { setShowForgot(true); setForgotEmail(form.email); setForgotSuccess(false); }}
                  >
                    <Lock size={14} /> {t('login.setPassword')}
                  </button>
                </div>
              )}

              {unverifiedEmail && !verificationResent && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <button
                    className="btn btn-outline"
                    style={{ fontSize: '13px', padding: '8px 16px' }}
                    onClick={async () => {
                      setResendingVerification(true);
                      try {
                        await resendVerification(unverifiedEmail);
                        setVerificationResent(true);
                        setError('');
                      } catch (err) {
                        setError(err.response?.data?.error || 'Failed to resend verification email');
                      } finally {
                        setResendingVerification(false);
                      }
                    }}
                    disabled={resendingVerification}
                  >
                    <Mail size={14} /> {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              )}

              {verificationResent && (
                <div style={{ background: '#DCFCE7', color: '#16A34A', padding: '12px', borderRadius: '8px', fontSize: '14px', textAlign: 'center', marginBottom: '16px' }}>
                  <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Verification email resent to <strong>{unverifiedEmail}</strong>. Check your inbox!
                </div>
              )}

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label><Mail size={16} /> {t('login.email')}</label>
                  <input
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><Lock size={16} /> {t('login.password')}</label>
                  <input
                    type="password"
                    placeholder={t('login.passwordPlaceholder')}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                  {loading ? t('login.signingIn') : t('login.signIn')}
                </button>
              </form>

              {/* Google sign-in is browser-only: Google rejects OAuth started
                  inside an embedded WebView (disallowed_useragent), and the
                  popup + postMessage handshake has no equivalent in the app. */}
              {!isNative() && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
                    <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                    <span style={{ fontSize: '13px', color: '#9ca3af' }}>{t('login.orContinueWith') || 'or continue with'}</span>
                    <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                  </div>

                  <button
                    type="button"
                    className="btn btn-block"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', fontWeight: 500, padding: '10px 16px', borderRadius: '8px', cursor: 'pointer' }}
                    disabled={loading}
                    onClick={() => { setError(''); setGoogleAccount(false); openGoogleSignIn(); }}
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                    Google
                  </button>
                </>
              )}

              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotEmail(form.email); setForgotSuccess(false); }}
                  style={{ background: 'none', border: 'none', color: '#be185d', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: '4px 8px' }}
                >
                  {t('login.forgotPassword')}
                </button>
              </div>

              <div className="auth-footer">
                <p>{t('login.noAccount')} <Link to={redirectTo ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register'}>{t('login.signUpFree')}</Link></p>
              </div>

              {/* Forgot Password Modal */}
              {showForgot && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                  onClick={() => setShowForgot(false)}>
                  <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
                    onClick={(e) => e.stopPropagation()}>
                    <h2 style={{ margin: '0 0 8px', fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>{t('login.resetPassword')}</h2>
                    <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6b7280' }}>{t('login.resetPasswordDesc')}</p>
                    {forgotSuccess ? (
                      <div style={{ background: '#DCFCE7', color: '#16A34A', padding: '16px', borderRadius: '8px', fontSize: '14px', textAlign: 'center' }}>
                        <CheckCircle size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        {t('login.resetSent')}
                      </div>
                    ) : (
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        setForgotLoading(true);
                        setForgotError('');
                        try {
                          await forgotPassword(forgotEmail);
                          setForgotSuccess(true);
                        } catch (err) {
                          // The server answers the same for known and unknown
                          // emails, so this is a rate limit or network failure.
                          setForgotError(err.response?.data?.error || t('resetPassword.genericError'));
                        } finally {
                          setForgotLoading(false);
                        }
                      }}>
                        {forgotError && <div className="auth-error" style={{ marginBottom: '12px' }}>{forgotError}</div>}
                        <div className="form-group" style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}><Mail size={16} /> {t('login.emailAddress')}</label>
                          <input
                            type="email"
                            placeholder="your@email.com"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            required
                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                          />
                        </div>
                        <button type="submit" className="btn btn-primary btn-block" disabled={forgotLoading}>
                          {forgotLoading ? t('login.sending') : t('login.sendResetLink')}
                        </button>
                      </form>
                    )}
                    <button
                      onClick={() => setShowForgot(false)}
                      style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: '#6b7280', fontSize: '13px', cursor: 'pointer' }}
                    >
                      {t('login.backToLogin')}
                    </button>
                  </div>
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
